import { useCallback, useEffect, useState } from "react";

import type { PublicState } from "../../shared/types";
import { getPublicState } from "../lib/api";

export function usePublicState() {
  const [state, setState] = useState<PublicState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await getPublicState(signal);
      setState(next);
      setError(null);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setError(caught instanceof Error ? caught.message : "The button state is unavailable.");
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const initialTimer = window.setTimeout(() => void refresh(controller.signal), 0);
    let timer = window.setInterval(() => void refresh(controller.signal), 3_000);

    const reschedule = () => {
      window.clearInterval(timer);
      timer = window.setInterval(
        () => void refresh(controller.signal),
        document.hidden ? 15_000 : 3_000,
      );
      if (!document.hidden) void refresh(controller.signal);
    };
    document.addEventListener("visibilitychange", reschedule);
    window.addEventListener("focus", reschedule);
    return () => {
      controller.abort();
      window.clearTimeout(initialTimer);
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", reschedule);
      window.removeEventListener("focus", reschedule);
    };
  }, [refresh]);

  return { state, error, refresh };
}
